/**
 * The two design-system values a drawing needs that theme.css does not carry.
 *
 * **The motion curves**, as their four control points. theme.css spells them
 * as `--ease-*` for CSS transitions; a canvas has to evaluate a curve itself,
 * so it reads the same tokens from the file that generates those variables.
 *
 * **The assistant's own colours.** `agent-colors.ts` is authored apart
 * from `colors.ts` on purpose (its header says why), so the generated theme
 * has no role for the orb's glass, its silhouette or its two blues. They are
 * read here from that file, by scheme, rather than written as a hex.
 *
 * Both are pure data (`agent-colors.ts` imports only `palette.ts`, which
 * imports nothing), so nothing React Native comes with them. They are reached
 * by a relative path, not `@ui/`, because `bun test` does not read the app's
 * path aliases and `motion.test.ts` loads this file.
 */
import { agentColors } from '../../../../packages/ui/src/theme/agent-colors';
import { alluzoEasing } from '../../../../packages/ui/src/theme/motion';

export type EasingName = keyof typeof alluzoEasing;

export const EASING_POINTS: Readonly<Record<EasingName, readonly [number, number, number, number]>> = {
  standard: alluzoEasing.standard.points,
  out: alluzoEasing.out.points,
  inOut: alluzoEasing.inOut.points,
  breathe: alluzoEasing.breathe.points,
  linear: alluzoEasing.linear.points,
};

/** The orb's glass, silhouette, deep blue and pale blue, as CSS colours, for one scheme. */
export type AgentCss = { readonly glass: string; readonly edge: string; readonly tint: string; readonly sky: string };

export const agentCss = (dark: boolean): AgentCss => {
  const scheme = dark ? agentColors.onDark : agentColors.onLight;
  return {
    glass: scheme.background.glass.intense,
    edge: scheme.border.glass.moderate,
    tint: scheme.background.orb.intense,
    sky: scheme.background.orb.moderate,
  };
};
